import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';
import VehicleTrackingDeliveryEditModal from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingDeliveryEditModal';
import { getAttachedDeliveriesForVehicleTracking } from 'src/redux/Actions/VehicleTrackingOutward';
import 'src/style/createVehicleTracking.css';

const getUnitOfMeasurement = (item) => item?.unit_of_measurement?.name;

const formatValueWithUom = (value, item) => {
  const uom = getUnitOfMeasurement(item);
  const displayValue =
    value !== null && value !== undefined && value !== '' ? value : '-';

  return uom && displayValue !== '-' ? `${displayValue} ${uom}` : displayValue;
};

let hasFetchedDelivery = false;

function DeliveryTab({
  deliveryLineItem,
  setDeliveryLineItem,
  organisationId,
  vehicleTrackingNumber,
  selectedStep,
  setSelectedStep,
  totalSteps,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [selectedDeliveryObjList, setSelectedDeliveryObjList] = useState([]);

  // Fetch attached deliveries and prefill deliveryLineItem, once.
  useEffect(() => {
    if (hasFetchedDelivery) return;
    if (!organisationId || !vehicleTrackingNumber) return;

    hasFetchedDelivery = true;

    dispatch(
      getAttachedDeliveriesForVehicleTracking(
        organisationId,
        vehicleTrackingNumber,
      ),
    ).then((data) => {
      if (data?.delivery) {
        setDeliveryLineItem(data.delivery);
      }
    });
  }, [dispatch, organisationId, vehicleTrackingNumber, setDeliveryLineItem]);

  const handleDelete = (deliveryId) => {
    setDeliveryLineItem((prev) =>
      (prev || []).filter(
        (deliveryObj) => deliveryObj.delivery_id !== deliveryId,
      ),
    );
  };

  const toggleEditDrawer = (open) => () => {
    setOpenEditDrawer(open);
    if (open) {
      setSelectedDeliveryObjList(deliveryLineItem || []);
    }
  };

  const handleUpdateDeliverySubmit = () => {
    setDeliveryLineItem(selectedDeliveryObjList);
    setOpenEditDrawer(false);
  };

  function handlePreviousStep() {
    setSelectedStep(selectedStep - 1);
  }

  function handleNextStep() {
    setSelectedStep(selectedStep + 1);
  }

  return (
    <>
      <div className="duplicate-delivery-tab vertical-padding-10 horizontal-padding-10">
        <div className="div-flex-row div-space-between section-title div-align-center">
          <div className="text-xxl text-bold">Delivery List</div>

          <button
            type="button"
            className={`common-btn-outlined theme-border-color-${themeColor}`}
            onClick={toggleEditDrawer(true)}
          >
            Add Delivery
          </button>
        </div>
        <div
          className={`po-table-wrapper delivery-table-fixed-container theme-scrollable-container-${themeColor}`}
        >
          <table>
            <thead className="outward-delivery-table-head">
              <tr>
                <th>Delivery Number</th>
                <th>Delivery Date</th>
                <th>Pack Type</th>
                <th>Pack Quantity</th>
                <th>Grade</th>
                <th>Gross Weight</th>
                <th>Product SAP Code</th>
                <th>Product Description</th>
                <th>Plant</th>
                <th>Net Weight</th>
                <th>Batch</th>
                <th>Sales Order Number</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(deliveryLineItem || []).length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    className="no-data-found-message border-bottom vertical-padding-10"
                  >
                    <span className="text-sm">No Delivery Items Found</span>
                  </td>
                </tr>
              ) : (
                deliveryLineItem.map((deliveryObj) => {
                  const lineItems = deliveryObj.line_items?.length
                    ? deliveryObj.line_items
                    : [{}];
                  const rowSpan = lineItems.length;

                  return lineItems.map((item, itemIndex) => (
                    <tr
                      key={
                        item?.delivery_line_item_id ||
                        `${deliveryObj.delivery_id}-${itemIndex}`
                      }
                    >
                      {itemIndex === 0 && (
                        <>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.sap_code ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.delivery_date ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.pack_type ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.pack_quantity ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>{deliveryObj?.grade ?? '-'}</td>
                          <td rowSpan={rowSpan}>
                            {formatValueWithUom(
                              deliveryObj?.weight,
                              deliveryObj,
                            )}
                          </td>
                        </>
                      )}
                      <td>{item?.product?.product_sap_code ?? '-'}</td>
                      <td>
                        {item?.product_description ||
                          item?.product?.product_description ||
                          '-'}
                      </td>
                      <td>{item?.plant?.name ?? '-'}</td>
                      <td>{formatValueWithUom(item?.quantity, item)}</td>
                      <td>{item?.batch ?? '-'}</td>
                      {itemIndex === 0 && (
                        <>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.sales_order_number ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            <div className="div-flex-row div-align-center">
                              <Delete
                                className={
                                  deliveryObj?.has_confirmed_load
                                    ? 'icon-size-lg icon-disabled'
                                    : 'icon-size-lg cursor-pointer'
                                }
                                onClick={() =>
                                  !deliveryObj?.has_confirmed_load &&
                                  handleDelete(deliveryObj.delivery_id)
                                }
                              />
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ));
                })
              )}
            </tbody>
          </table>
        </div>

        <Drawer
          anchor="right"
          open={Boolean(openEditDrawer)}
          onClose={toggleEditDrawer(false)}
          ModalProps={{ keepMounted: false }}
          sx={{ zIndex: 1 }}
          PaperProps={{
            sx: { width: '80%' },
          }}
        >
          <div className="div-flex-column rg-20 container-padding-20">
            <span className="text-xxl text-bold">Update Delivery</span>
            <VehicleTrackingDeliveryEditModal
              themeColor={themeColor}
              selectedDeliveryObjList={selectedDeliveryObjList}
              setSelectedDeliveryObjList={setSelectedDeliveryObjList}
              deliveryHeaderData={deliveryLineItem || []}
              toggleEditDrawer={toggleEditDrawer}
              handleUpdateDeliverySubmit={handleUpdateDeliverySubmit}
              isSubmitting={false}
              lockConfirmedDeliveries={false}
            />
          </div>
        </Drawer>
      </div>

      <div className=" div-flex-row div-space-between div-align-center vertical-padding-10 horizontal-padding-10 border-top">
        <button
          type="button"
          className={`common-btn-outlined theme-border-color-${themeColor}`}
          onClick={handlePreviousStep}
          disabled={selectedStep === 1}
        >
          Back
        </button>
        <button
          type="button"
          className={`common-btn theme-background-${themeColor}`}
          onClick={handleNextStep}
          disabled={selectedStep === totalSteps}
        >
          Next
        </button>
      </div>
    </>
  );
}

export default DeliveryTab;
